'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Rocket, CheckCircle2 } from 'lucide-react';
import { ApiInitiative, initiativesApi } from '@/lib/api';
import { useSession } from '@/lib/auth-client';
import { useSignInModal } from '@/lib/ui';
import clsx from 'clsx';
interface InitiativeParticipationActionProps {
  initiative: ApiInitiative;
  onSuccess?: () => void;
}
export function InitiativeParticipationAction(t0) {
  const $ = _c(30);
  const {
    initiative,
    onSuccess
  } = t0;
  const {
    data: session
  } = useSession();
  const {
    showSignInModal
  } = useSignInModal();
  const [hasParticipated, setHasParticipated] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      label: "Take Quiz",
      icon: <Rocket className="w-5 h-5 mr-2" />
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      label: "Enter Competition",
      icon: <Rocket className="w-5 h-5 mr-2" />
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      label: "Join Giveaway",
      icon: <Rocket className="w-5 h-5 mr-2" />
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      label: "Claim Offer",
      icon: <Rocket className="w-5 h-5 mr-2" />
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      quiz: t1,
      competition: t2,
      giveaway: t3,
      discount: t4,
      campaign: {
        label: "Join Campaign",
        icon: <Rocket className="w-5 h-5 mr-2" />
      }
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const actionConfig = t5;
  let t6;
  if ($[5] !== initiative.type) {
    t6 = actionConfig[initiative.type] || {
      label: "Participate Now",
      icon: <Rocket className="w-5 h-5 mr-2" />
    };
    $[5] = initiative.type;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const config = t6;
  let t7;
  if ($[7] !== initiative.id) {
    t7 = async () => initiativesApi.participate(initiative.id);
    $[7] = initiative.id;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== onSuccess) {
    t8 = () => {
      setHasParticipated(true);
      toast.success("Successfully registered your participation!");
      onSuccess?.();
    };
    $[9] = onSuccess;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = {
      mutationFn: t7,
      onSuccess: t8,
      onError: _temp
    };
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const participateMutation = useMutation(t9);
  let t10;
  if ($[14] !== participateMutation || $[15] !== session?.user || $[16] !== showSignInModal) {
    t10 = () => {
      if (!session?.user) {
        showSignInModal({
          onSuccess: () => {
            participateMutation.mutate();
          }
        });
        return;
      }
      participateMutation.mutate();
    };
    $[14] = participateMutation;
    $[15] = session?.user;
    $[16] = showSignInModal;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleAction = t10;
  if (hasParticipated) {
    let t11;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <button disabled={true} className="flex items-center justify-center w-full py-3 px-6 bg-green-50 text-green-700 font-semibold rounded-xl border border-green-200 transition-colors"><CheckCircle2 className="w-5 h-5 mr-2" />You're In!</button>;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    return t11;
  }
  const t11 = participateMutation.isPending || initiative.status !== "active";
  const t12 = initiative.status === "active" ? "bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-75" : "bg-gray-200 text-gray-500 cursor-not-allowed";
  let t13;
  if ($[19] !== t12) {
    t13 = clsx("flex items-center justify-center w-full py-3 px-6 font-semibold rounded-xl transition-colors shadow-sm", t12);
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== config || $[22] !== initiative.status || $[23] !== participateMutation.isPending) {
    t14 = participateMutation.isPending ? <span className="flex items-center"><svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Processing...</span> : <>{config.icon}{initiative.status === "active" ? config.label : "Currently Unavailable"}</>;
    $[21] = config;
    $[22] = initiative.status;
    $[23] = participateMutation.isPending;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== handleAction || $[26] !== t11 || $[27] !== t13 || $[28] !== t14) {
    t15 = <button onClick={handleAction} disabled={t11} className={t13}>{t14}</button>;
    $[25] = handleAction;
    $[26] = t11;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
}
function _temp(error) {
  toast.error(error.message || "Failed to participate. Please try again later.");
}